import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import {
  Search,
  LayoutTemplate,
  UserCog,
  ArrowRight,
  Activity,
  Shield,
} from 'lucide-react'
import { useAuthStore } from '~/client/common/stores/auth'

export const Route = createFileRoute('/admin/')({
  beforeLoad: () => {
    if (typeof window === 'undefined') return
    const { userDetail } = useAuthStore.getState()
    // Non-superuser staff (entity 47) bypass the dashboard — recs is their
    // only allowed surface.
    if (userDetail && !userDetail.is_superuser) {
      throw redirect({
        to: '/admin/recs',
        search: { eventId: undefined, tab: undefined, status: undefined, city: undefined, sort: undefined },
      })
    }
  },
  component: AdminDashboard,
})

const QUICK_LINKS = [
  {
    title: 'Typesense Comparison',
    description:
      'Compare search results between Supabase and Typesense indexes to identify discrepancies.',
    href: '/admin/typesense',
    icon: Search,
    color: 'bg-blue-50 text-blue-600 border-blue-200',
    iconBg: 'bg-blue-100',
  },
  {
    title: 'Template Editor',
    description:
      'Edit and preview presentation slide templates with a visual WYSIWYG editor.',
    href: '/admin/template-editor',
    icon: LayoutTemplate,
    color: 'bg-purple-50 text-purple-600 border-purple-200',
    iconBg: 'bg-purple-100',
  },
  {
    title: 'Impersonate User',
    description:
      'Sign in as another user to debug issues or verify their experience.',
    href: '/admin/impersonate',
    icon: UserCog,
    color: 'bg-amber-50 text-amber-600 border-amber-200',
    iconBg: 'bg-amber-100',
  },
] as const

function AdminDashboard() {
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Admin Dashboard
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage internal tools, debug search indexes, and perform admin
          operations.
        </p>
      </div>

      {/* Status Banner */}
      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
        <Activity className="size-4 text-green-600" />
        <span className="text-sm font-medium text-green-800">
          All systems operational
        </span>
      </div>

      {/* Quick Links Grid */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Quick Access
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {QUICK_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`group relative flex flex-col gap-3 rounded-xl border p-5 transition-all hover:shadow-md hover:border-gray-300 ${link.color}`}
            >
              <div
                className={`flex size-10 items-center justify-center rounded-lg ${link.iconBg}`}
              >
                <link.icon className="size-5" />
              </div>
              <div className="space-y-1">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-1">
                  {link.title}
                  <ArrowRight className="size-3.5 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {link.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="rounded-lg border bg-muted/30 p-5">
        <div className="flex items-start gap-3">
          <Shield className="size-5 text-muted-foreground mt-0.5" />
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-foreground">
              Admin Access
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              This area is restricted to superusers only. All actions performed
              here are logged. Use the sidebar to navigate between admin tools,
              or use the Quick Admin Dock at the bottom of any page for fast
              actions.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
